/**
 * CuerpoMensaje — el contenido de una burbuja del inbox: imagen, sticker, audio o texto.
 *
 * Paso 5 del Bloque E. Se extrajo de `ChatDetalle.js:924-954`, donde el contenido era un
 * ternario de DOS ramas dentro de otro ternario, a seis niveles de anidación de JSX.
 * Añadir imagen y sticker allí habría hecho un ternario anidado de cuatro ramas; aquí son
 * cuatro casos legibles, y —lo que decidió la extracción— **testeables en aislamiento**.
 * Los cuatro estados de carga son justamente lo que hay que probar.
 *
 * ⚠️ DISCRIMINA POR `adjuntos[0].type`, NO POR `msg.tipo`.
 * Verificado con datos reales (2026-07-30):
 *   foto    → tipo: "imagen",  adjuntos[0].type: "image"
 *   audio   → tipo: "audio",   adjuntos[0].type: "audio"
 *   sticker → tipo: "archivo", adjuntos[0].type: "sticker"   ← `tipo` no lo identifica
 * `adjuntos[0].type` es una sola regla uniforme; `msg.tipo` mezcla español con un cajón
 * genérico y exigiría tres.
 *
 * ⚠️ `msg.type === 'audio'` RETIRADO. Era el lector de la era Baileys y en 83 mensajes
 * reales hay CERO con `type==='audio'` y CERO con `audioUrl`. Mantener una condición para
 * un camino sin datos cubre lo que no existe y confunde a quien lo lee. Si vuelve
 * Baileys, se añade entonces con datos delante.
 *
 * LA TRANSCRIPCIÓN DEL AUDIO se pinta bajo el reproductor desde el 24-sep-2026. Estaba
 * anotada aquí como pendiente: vivía en `mediaTexts.mediaText`, el callable la leía —en
 * la MISMA lectura que usa para firmar— y no la devolvía.
 *
 * ⚠️ SÓLO PARA AUDIO, y la asimetría es deliberada. `mediaText` lleva también la
 * descripción de las imágenes (`(imagen recibida: …)`, hasta 320 caracteres medidos), y
 * ahí NO se pinta: el operador ya ve la foto. Lo que esa descripción aporta es saber qué
 * entendió el bot — valor de diagnóstico, no de operación. El día que haga falta, entra
 * plegada. Se discrimina por `adjuntos[0].type`, que es la regla que este fichero ya usa,
 * y no parseando el prefijo del texto.
 */

import React, { useEffect } from 'react';
import { esComentario, esRespuestaPrivada } from '../../utils/mensajeDeCliente';
import { esEchoSinTexto, burbujaDeEcho } from '../../utils/echoDeAppBusiness';

/**
 * 'image' | 'sticker' | 'audio' | 'video' | 'enlace' | 'aviso' | 'productos' | 'echo-app-business' | 'texto' | 'sin-contenido'
 *
 * `producto` (13-oct-2026) es el carrusel de tarjetas que manda el bot (`tarjetasDeProducto.js`
 * en functions): un documento con un adjunto por tarjeta.
 *
 * `ig_post` —una publicación de Instagram compartida— se mapea a `'image'` PARA
 * RENDERIZAR, porque lo que trae es su miniatura y se pinta igual que una foto. El
 * `type` del adjunto SIGUE siendo `ig_post` en Firestore: la distinción entre una foto
 * enviada y una publicación compartida es información de negocio y no se destruye.
 * Aquí sólo se decide con qué elemento se pinta.
 *
 * `ig_reel` y `share_post` son lo contrario: no hay miniatura NI fichero. Lo único
 * que llega es una URL a una página web —`www.instagram.com/reel/…` devuelve
 * `text/html`— así que se pintan como enlace. Ver `ENLACES` abajo.
 *
 * ⚠️ `share` NO está aquí, y no por descuido. Es MEDIA de CDN: su único caso real
 * (8-ago-2026) trae `lookaside.fbsbx.com/ig_messaging_cdn/?asset_id=…`, el mismo
 * host que `ig_post`. Cae a `'texto'` como antes, pero por un motivo distinto al
 * que decía este comentario hasta el 14-sep: no es una página web, es media cuya
 * ingesta está pendiente de su propia decisión. Su URL además caducó a los 37 días.
 */
export function clasificar(msg) {
  // ⚠️ VA ANTES DE MIRAR `adjuntos`, y no es orden arbitrario: un comentario de
  // Instagram no trae ninguno, así que la clasificación por adjunto no lo vería
  // nunca y caería a 'texto' — indistinguible de un DM, que es el fallo que este
  // commit corrige. Lo mismo la respuesta privada, que es texto puro.
  if (esComentario(msg)) return 'comentario';
  if (esRespuestaPrivada(msg)) return 'respuesta-privada';
  // ECHO SIN TEXTO de la app WhatsApp Business (coexistence C3d, 27-oct-2026). También ANTES de
  // mirar `adjuntos`: el escritor no guarda ninguno (el binario se queda en el teléfono del
  // negocio) y el documento caería a 'texto' pintando el marcador `[audio]` tal cual.
  if (esEchoSinTexto(msg)) return 'echo-app-business';
  const t = (msg?.adjuntos || [])[0]?.type;
  if (t === 'ig_post') return 'image';
  if (ENLACES[t]) return 'enlace';
  if (AVISOS[t]) return 'aviso';
  if (t === 'producto') return 'productos';
  if (t === 'image' || t === 'sticker' || t === 'audio' || t === 'video') return t;
  // DOCUMENTO de WhatsApp (B1.2, 20-oct-2026). Hasta hoy caía a 'texto' y se pintaba
  // «[document]» sin nombre ni descarga. El `file` de Instagram NO entra aquí: es un
  // aviso con enlace a la CDN de Meta (ver AVISOS), sin binario propio.
  if (t === 'document') return 'documento';
  // NINGUNA BURBUJA VACÍA (15-oct-2026). Lo que cae a texto sin texto se pinta como
  // «sin contenido visible», con su tipo: en 30 días fueron 15 entrantes (Instagram y TikTok).
  return String(msg?.text || msg?.mensaje || msg?.contenido || '').trim() ? 'texto' : 'sin-contenido';
}

/**
 * ADJUNTOS DE INSTAGRAM QUE NO SON MEDIA PINTABLE (15-oct-2026). El caso: un cliente de
 * Bigwaves mencionó a la tienda en su historia y el Inbox mostró una burbuja vacía. Medido
 * en 30 días, todas las organizaciones: `story_mention` 1, `template` 6, `file` 1.
 *
 *  · `story_mention`: Meta sólo manda el link al video o la foto de la historia, en su CDN, y
 *    caduca con la historia (~24 h). Se ofrece mientras funcione.
 *  · `template`: una publicación o un producto compartido. Hasta el 15-oct el normalizador
 *    no guardaba nada de su contenido; desde entonces guarda `titulo` y `url` si vienen.
 *  · `file`: un archivo; la URL es del CDN de Meta.
 */
const AVISOS = {
  story_mention: { icono: '📣', clave: 'detail.storyMention', claveLink: 'detail.storyMentionLink' },
  template: { icono: '🔗', clave: 'detail.sharedTemplate', claveLink: 'detail.openLink' },
  file: { icono: '📎', clave: 'detail.sharedFile', claveLink: 'detail.openFile' },
};

/**
 * Los dos tipos que son una página web, con qué etiqueta se pinta cada uno.
 *
 * UNA TABLA Y NO DOS `if`: el operador tiene que poder distinguir de un vistazo un
 * reel de Instagram de una publicación de TikTok, y esa distinción es el único
 * contenido que esta burbuja aporta —no hay título, ni miniatura, ni autor—. Con
 * dos ramas sueltas, añadir una plataforma es tocar dos sitios y olvidarse de uno.
 */
const ENLACES = {
  ig_reel: { icono: '🎬', clave: 'detail.sharedReel' },
  share_post: { icono: '🎵', clave: 'detail.sharedTikTok' },
};

/**
 * La URL, legible. `https://www.instagram.com/reel/DbZeTIjJE7x/` -> `instagram.com/reel/DbZeTIjJE7x`
 *
 * NO es cosmética: el `embed_url` de TikTok mide 138 caracteres (medido sobre 6
 * capturas reales del log de estructura), y pintarlo entero ocupa tres líneas de
 * burbuja para no decir nada — la query no la lee nadie. El `href` conserva la URL
 * COMPLETA; esto sólo afecta a lo que se ve.
 *
 * Si la URL no parsea se devuelve tal cual: es preferible una cadena rara visible a
 * una burbuja vacía, y el `href` sigue funcionando.
 */
export function urlLegible(url) {
  if (typeof url !== 'string' || !url) return '';
  try {
    const u = new URL(url);
    return (u.host.replace(/^www\./, '') + u.pathname).replace(/\/$/, '');
  } catch {
    return url;
  }
}

/**
 * Caja de proporción FIJA para el placeholder.
 * No guardamos ancho/alto del original, así que no se puede reservar la proporción real y
 * habrá un salto al cargar. Guardar width/height en la subida (paso 3) lo evitaría —
 * anotado como deuda. Con tamaño fijo el salto es acotado y predecible.
 */
const CAJA = { width: 220, height: 165 };

const cajaBase = {
  ...CAJA,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: 8, fontSize: '0.8rem', textAlign: 'center', padding: 8,
  boxSizing: 'border-box',
};

/**
 * El ícono de un documento, por su MIME (el que declaró Meta y guardó el normalizador).
 * Más de dos ramas y sin estado: por eso es una función pura y se prueba sola.
 * Mime desconocido o ausente → 📎, nunca una burbuja sin ícono.
 */
export function iconoDeDocumento(mime) {
  const m = String(mime || '').split(';')[0].trim().toLowerCase();
  if (m === 'application/pdf') return '📕';
  if (m === 'application/msword' || m.includes('wordprocessingml')) return '📘';
  if (m === 'application/vnd.ms-excel' || m.includes('spreadsheetml')) return '📗';
  if (m === 'application/vnd.ms-powerpoint' || m.includes('presentationml')) return '📙';
  if (m.startsWith('image/')) return '🖼️';
  if (m === 'text/plain') return '📄';
  return '📎';
}

/** Bytes legibles: «850 B», «12 KB», «3,4 MB». null/0 → null (no se pinta). */
export function tamanoLegible(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

export default function CuerpoMensaje({ msg, styles, t, media }) {
  const clase = clasificar(msg);
  const texto = msg.text || msg.mensaje || msg.contenido;

  // ⚠️ VA ANTES DEL RETURN DE TEXTO: un hook no puede ir después de una salida
  // condicional. Para un mensaje de texto el efecto no hace nada —`mediaListo` no
  // existe— pero tiene que EJECUTARSE igual, o React vería un número de hooks distinto
  // según la rama.
  //
  // El pipeline tarda 1-15 s en subir el binario y el observer pregunta en cuanto la
  // burbuja aparece. Cuando el servidor termina escribe `mediaListo: true` en el doc del
  // mensaje; ese cambio llega por onSnapshot y aquí se vuelve a pedir la URL.
  // Sin esto el media sólo se ve recargando la página — y el operador que mira el chat
  // EN VIVO, que es el caso de uso, no tiene forma de saber que debe recargar.
  // La dependencia es la TRANSICIÓN de mediaListo, así que dispara una vez, no en bucle.
  useEffect(() => {
    if (msg.mediaListo) media.refrescar(msg.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg.mediaListo, msg.id]);

  if (clase === 'texto') {
    return <div className={styles.bubbleText}>{texto}</div>;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  COMENTARIO Y RESPUESTA PRIVADA — van ANTES de tocar `media`, como el enlace:
  //  ninguno tiene `storagePath`, y consultarlo encolaría una petición al callable
  //  por burbuja para acabar en `sin-media`.
  //
  //  El comentario se pinta CON SU CONTEXTO. El dato ya estaba guardado y completo
  //  —`meta.mediaProductType` decía `REELS` en el caso del 21-sep— y nadie lo
  //  miraba: el operador veía un DM que la persona nunca escribió.
  //
  //  Y la respuesta privada lleva marca propia porque se persiste con
  //  `remitente: 'bot'` —lo es— y sin ella sería indistinguible de una respuesta
  //  del bot en una conversación. Son dos cosas distintas: una la dijo la
  //  automatización respondiendo a un comentario, la otra el bot hablando.
  // ═══════════════════════════════════════════════════════════════════════════
  if (clase === 'comentario') {
    const tipo = msg.meta?.mediaProductType;
    const clave = tipo === 'REELS' ? 'detail.comentarioEnReel' : 'detail.comentarioEnPublicacion';
    return (
      <div data-testid="comentario-inbox" className={styles.bubbleText}>
        <div style={{ fontWeight: 600, fontSize: '0.8em', opacity: 0.75, marginBottom: 2 }}>
          💬 {t(clave)}
        </div>
        {texto}
      </div>
    );
  }

  if (clase === 'respuesta-privada') {
    return (
      <div data-testid="respuesta-privada-inbox" className={styles.bubbleText}>
        <div style={{ fontWeight: 600, fontSize: '0.8em', opacity: 0.75, marginBottom: 2 }}>
          ↩️ {t('detail.respuestaPrivada')}
        </div>
        {texto}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  ECHO SIN TEXTO DE LA APP WHATSAPP BUSINESS (coexistence C3d, 27-oct-2026) — ANTES de
  //  tocar `media`, por lo mismo que el enlace: no hay `storagePath` ni binario que firmar,
  //  y consultarlo encolaría una petición al callable para acabar en «Archivo no disponible».
  //
  //  SIN REPRODUCTOR NI DESCARGA, a propósito: la nota de voz o la foto están en el teléfono
  //  del negocio, no en Storage, y prometer un reproductor que nunca va a cargar es peor que
  //  decir dónde está. El operador ve QUÉ mandó el equipo y, si lo hay, el caption o el
  //  nombre del fichero (de `meta.objetoDeMeta`, no del texto).
  //
  //  ⚠️ `texto` NO se pinta: es el marcador del escritor (`[audio]`), el fallo que esta rama
  //  arregla. Un echo de TEXTO del titular no entra aquí (`esEchoSinTexto` lo excluye) y sigue
  //  por la rama de texto, igual que hoy.
  // ═══════════════════════════════════════════════════════════════════════════
  if (clase === 'echo-app-business') {
    const b = burbujaDeEcho(msg);
    return (
      <div data-testid="echo-app-business-inbox" data-tipo={b.tipo} className={styles.bubbleText}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{b.icono} {t(b.clave)}</div>
        {b.nombre ? <div data-testid="echo-nombre" style={{ fontSize: '0.9em' }}>{b.nombre}</div> : null}
        {b.caption ? (
          <div data-testid="echo-caption" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{b.caption}</div>
        ) : null}
        {b.soloEnElTelefono ? (
          // `#495057` como la transcripción: el gris claro daba 2,9:1 sobre el verde de salida.
          <div data-testid="echo-solo-en-el-telefono" style={{ fontSize: '0.8em', color: '#495057', fontStyle: 'italic', marginTop: 2 }}>
            {t('detail.echoSoloEnElTelefono')}
          </div>
        ) : null}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  ENLACE — va AQUÍ, ANTES de tocar `media`, y el orden es lo importante.
  //
  //  Un enlace no tiene `storagePath`: el callable `getInboxMediaUrls` sólo firma
  //  los mediaTexts que lo llevan. Si esta rama fuera después, cada burbuja de
  //  enlace llamaría a `media.estado(id)` y `media.observar(id)`, encolaría una
  //  petición al callable por algo que nunca se subió, y el resultado sería
  //  `sin-media` — «Archivo no disponible» sobre un enlace que está perfectamente
  //  disponible. Una petición de red por burbuja para pintar un error falso.
  //
  //  El `useEffect` de arriba sí corre, y debe: está guardado por `msg.mediaListo`,
  //  que un enlace no tiene, así que no hace nada. Lo que no puede es saltarse la
  //  llamada al hook.
  //
  //  TARJETA NO, ENLACE SÍ. Sin título, sin miniatura y sin autor, una tarjeta es
  //  una caja con una URL dentro: el mismo cromo, la misma información, y encima
  //  reutilizaría el lenguaje visual de `cajaBase` —que en este inbox significa
  //  «hay una imagen cargando»— prometiendo algo que no va a llegar nunca.
  // ═══════════════════════════════════════════════════════════════════════════
  if (clase === 'enlace') {
    const adj = (msg.adjuntos || [])[0] || {};
    const { icono, clave } = ENLACES[adj.type];
    const legible = urlLegible(adj.url);
    return (
      <div data-testid="enlace-inbox" data-tipo={adj.type} className={styles.bubbleText}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{icono} {t(clave)}</div>
        {adj.url ? (
          <a
            href={adj.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ wordBreak: 'break-all', fontSize: '0.85em' }}
          >
            {legible}
          </a>
        ) : (
          // Sin URL no se pinta un enlace muerto. `ephemeral` ya llegó con `url: null`
          // una vez, así que el caso no es hipotético.
          <span style={{ fontSize: '0.85em', color: '#868e96', fontStyle: 'italic' }}>
            {t('detail.mediaUnavailable')}
          </span>
        )}
      </div>
    );
  }

  // AVISO y SIN CONTENIDO (15-oct-2026) — tampoco tocan `media`: no hay nada subido a Storage.
  if (clase === 'aviso') {
    const adj = (msg.adjuntos || [])[0] || {};
    const { icono, clave, claveLink } = AVISOS[adj.type];
    const url = typeof adj.url === 'string' && /^https:\/\//i.test(adj.url) ? adj.url : null;
    return (
      <div data-testid="aviso-inbox" data-tipo={adj.type} className={styles.bubbleText}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{icono} {t(clave)}</div>
        {adj.titulo ? <div style={{ fontSize: '0.9em' }}>{adj.titulo}</div> : null}
        {texto ? <div>{texto}</div> : null}
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85em' }}>
            {t(claveLink)}
          </a>
        ) : null}
      </div>
    );
  }
  if (clase === 'sin-contenido') {
    const tipo = (msg.adjuntos || [])[0]?.type || null;
    return (
      <div data-testid="sin-contenido-inbox" data-tipo={tipo || ''} className={styles.bubbleText}
        style={{ color: '#868e96', fontStyle: 'italic' }}>
        {t('detail.sinContenido')}{tipo ? ` (${tipo})` : ''}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  FOTO DE CATÁLOGO (13-oct-2026) — también ANTES de tocar `media`, por lo mismo que el
  //  enlace: la manda el bot desde la página del producto (`fotosDeProducto.js` en
  //  functions), no se sube a Storage y no hay nada que firmar. Se pinta su URL tal cual
  //  (la CSP de la app admite `img-src https:`), con el nombre del producto debajo. Sólo
  //  con `origen: 'catalogo'` y https: cualquier otra imagen sigue su camino de siempre.
  // ═══════════════════════════════════════════════════════════════════════════
  const adjuntoDeCatalogo = (msg.adjuntos || [])[0];
  if (clase === 'image' && adjuntoDeCatalogo?.origen === 'catalogo'
    && typeof adjuntoDeCatalogo.url === 'string' && /^https:\/\//i.test(adjuntoDeCatalogo.url)) {
    return (
      <figure data-testid="imagen-catalogo" style={{ margin: 0 }}>
        <img
          src={adjuntoDeCatalogo.url}
          alt={adjuntoDeCatalogo.nombre || t('detail.image')}
          loading="lazy"
          style={{ ...CAJA, objectFit: 'cover', borderRadius: 8, display: 'block', background: '#f1f3f5' }}
        />
        {adjuntoDeCatalogo.nombre ? (
          <figcaption style={{ fontSize: '0.8em', opacity: 0.75, marginTop: 4 }}>{adjuntoDeCatalogo.nombre}</figcaption>
        ) : null}
      </figure>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  TARJETAS DE PRODUCTO (13-oct-2026) — ANTES de tocar `media`, como la foto de catálogo:
  //  todo viene del catálogo de la tienda y no hay nada que firmar. Una fila que se
  //  desplaza, como el carrusel que vio el cliente: foto, nombre, precio y link. Imagen y
  //  link sólo si son https.
  // ═══════════════════════════════════════════════════════════════════════════
  if (clase === 'productos') {
    const esHttps = (u) => typeof u === 'string' && /^https:\/\//i.test(u);
    const tarjetas = (msg.adjuntos || []).filter((a) => a && a.type === 'producto');
    return (
      <div data-testid="tarjetas-catalogo" style={{ display: 'flex', gap: 8, overflowX: 'auto', maxWidth: '100%', paddingBottom: 4 }}>
        {tarjetas.map((a, i) => (
          <div key={a.productId || i} data-testid="tarjeta-catalogo"
            style={{ flex: '0 0 140px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            {esHttps(a.imagen) ? (
              <img src={a.imagen} alt={a.nombre || t('detail.image')} loading="lazy"
                style={{ width: 140, height: 140, objectFit: 'cover', display: 'block', background: '#f1f3f5' }} />
            ) : null}
            <div style={{ padding: 6, fontSize: '0.8em' }}>
              <div style={{ fontWeight: 600 }}>{a.nombre}</div>
              {a.precio ? <div style={{ opacity: 0.75 }}>{a.precio}</div> : null}
              {esHttps(a.url) ? (
                <a href={a.url} target="_blank" rel="noopener noreferrer">{t('detail.verProducto')}</a>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const estado = media.estado(msg.id);
  const url = media.url(msg.id);

  // El ref va SIEMPRE en el nodo, también en los estados sin imagen: es lo que hace que
  // el observer pida la URL cuando entra en pantalla. Sin ref en `pendiente`, nunca se
  // pediría nada y el estado no cambiaría jamás.
  const ref = media.observar(msg.id);

  if (clase === 'documento') {
    // MISMO ciclo de carga que el resto del media: la URL la firma `getInboxMediaUrls`.
    // La diferencia está en la firma, no aquí: para un documento lleva el
    // `Content-Disposition`, así que el enlace DESCARGA con el nombre del cliente y sólo
    // un PDF se abre en otra pestaña (`utils/disposicionDeDescarga.js`). Por eso el
    // enlace no lleva atributo `download`: entre orígenes distintos el navegador lo
    // ignora, y la cabecera es la que manda.
    //
    // El NOMBRE y el CAPTION salen del adjunto, que no caduca; el binario se retiene 30
    // días. Pasado ese plazo la burbuja sigue diciendo qué era, sin enlace.
    const adj = (msg.adjuntos || [])[0] || {};
    const nombre = adj.name && adj.name !== 'document' ? adj.name : t('detail.document');
    const tamano = tamanoLegible(media.tamano?.(msg.id));
    const esPdf = String(adj.mime || '').toLowerCase().startsWith('application/pdf');
    return (
      <div ref={ref} data-testid="documento-inbox" data-estado={estado}
        style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 280 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
          background: '#f1f3f5', borderRadius: 8,
        }}>
          <span aria-hidden="true" style={{ fontSize: '1.6rem', lineHeight: 1 }}>{iconoDeDocumento(adj.mime)}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={nombre}>{nombre}</div>
            <div style={{ fontSize: '0.8em', color: '#868e96' }}>
              {estado === 'ok' ? (
                <>
                  {tamano ? <span>{tamano} · </span> : null}
                  <a data-testid="documento-descargar" href={url} target="_blank" rel="noopener noreferrer">
                    {esPdf ? t('detail.openDocument') : t('detail.downloadDocument')}
                  </a>
                </>
              ) : estado === 'sin-media' ? t('detail.mediaUnavailable')
                : estado === 'error' ? t('detail.mediaError')
                  : t('detail.processingDocument')}
            </div>
          </div>
        </div>
        {adj.caption ? (
          <div data-testid="documento-caption" style={{ fontSize: '0.9em', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {adj.caption}
          </div>
        ) : null}
      </div>
    );
  }

  if (clase === 'audio') {
    return (
      <div className={styles.audioContainer} ref={ref}>
        <div className={styles.audioHeader}>
          <span style={{ fontSize: '1.2rem' }}>🎤</span>
          <span>{t('detail.audioMessage')}</span>
        </div>
        {estado === 'ok' ? (
          <audio
            data-testid="audio-inbox"
            controls
            className={styles.audioPlayer}
            src={url}
            onError={() => media.alFallar(msg.id)}
            onLoadedData={() => media.alCargar(msg.id)}
          >
            {t('detail.audioNotSupported')}
          </audio>
        ) : (
          <div style={{ fontSize: '0.85em', color: '#999', fontStyle: 'italic' }}>
            {estado === 'sin-media' ? t('detail.mediaUnavailable')
              : estado === 'error' ? t('detail.mediaError')
                : t('detail.processingAudio')}
          </div>
        )}
        {/* LA TRANSCRIPCIÓN · un audio hay que ESCUCHARLO para saber qué dice; esto se
            lee en dos segundos. El dato ya existía en `mediaTexts` y el callable lo
            leía sin devolverlo (ver `getInboxMediaUrls.js`).

            SIEMPRE VISIBLE, sin «ver más»: medido el 24-sep-2026, p50 = 111 caracteres
            —línea y media, más corto que muchos mensajes de texto que esta misma
            burbuja pinta enteros— y el máximo son 525. Un control de despliegue para
            111 caracteres es fricción sin motivo.

            ⚠️ FUERA DEL TERNARIO DE `estado`, y es el punto del diseño: se pinta aunque
            el audio sea `sin-media` o `error`. El binario se retiene 30 días y el texto
            no caduca, así que en un audio viejo la transcripción es LO ÚNICO que queda.

            El prefijo «(nota de voz) » viene dentro del propio texto —se guarda así
            porque esa cadena es la que va al modelo— y NO se recorta: un regex sobre un
            dato guardado es la clase de parseo que rompe en silencio. */}
        {media.texto?.(msg.id) && (
          <div
            data-testid="transcripcion-inbox"
            style={{
              marginTop: '0.4rem', fontSize: '0.85em', lineHeight: 1.35,
              color: '#495057', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}
          >
            {media.texto(msg.id)}
          </div>
        )}
        {/* LA TRANSCRIPCIÓN NO VERIFICADA (20-oct-2026): el filtro de Whisper la rechazó por
            parecer alucinada, y el bot pidió repetir. El operador la ve igual, MARCADA, para
            decidir él; el modelo nunca la recibe. Sólo si no hay una verificada. */}
        {!media.texto?.(msg.id) && media.textoNoVerificado?.(msg.id) && (
          <div
            data-testid="transcripcion-no-verificada"
            style={{
              marginTop: '0.4rem', fontSize: '0.85em', lineHeight: 1.35,
              color: '#868e96', fontStyle: 'italic', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}
          >
            <span style={{ fontWeight: 600, fontStyle: 'normal' }}>{t('detail.unverifiedTranscript')}: </span>
            {media.textoNoVerificado(msg.id)}
          </div>
        )}
      </div>
    );
  }

  if (clase === 'video') {
    // MISMO ciclo de carga que el audio: la URL la firma `getInboxMediaUrls`, que es
    // agnóstico al tipo — sólo firma un `storagePath`. El `content-type` lo sirve
    // Storage desde el mime con que se subió (`video/mp4`).
    //
    // SIN `poster`, y es decisión, no olvido: generar una miniatura exige extraer un
    // frame, y eso necesita ffmpeg, que NO existe en el runtime de Cloud Functions
    // gen2 (documentado en functions/bot/mediaUnderstanding.js:26-28). Es el mismo
    // motivo por el que el vídeo no pasa por visión. Sin `poster`, el navegador pinta
    // el primer frame al cargar los metadatos: exactamente lo que queremos, y gratis.
    //
    // `preload="metadata"` y no `auto`: un chat con varios vídeos descargaría megas
    // que nadie ha pedido ver. Con metadata baja sólo la cabecera, suficiente para el
    // primer frame y la duración.
    return (
      <div ref={ref} style={{ ...CAJA, borderRadius: 8, overflow: 'hidden', background: '#000' }}>
        {estado === 'ok' ? (
          <video
            data-testid="video-inbox"
            controls
            preload="metadata"
            src={url}
            onError={() => media.alFallar(msg.id)}
            onLoadedMetadata={() => media.alCargar(msg.id)}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          >
            {t('detail.videoNotSupported')}
          </video>
        ) : (
          <div style={{ ...cajaBase, background: '#f1f3f5', color: '#868e96' }}>
            {estado === 'sin-media' ? t('detail.mediaUnavailable')
              : estado === 'error' ? t('detail.mediaError')
                : t('detail.processingVideo')}
          </div>
        )}
      </div>
    );
  }

  // imagen y sticker: mismo ciclo de carga, distinto tamaño y encaje.
  const esSticker = clase === 'sticker';
  const dims = esSticker ? { width: 130, height: 130 } : CAJA;

  if (estado === 'ok') {
    return (
      <img
        data-testid="imagen-inbox"
        ref={ref}
        src={url}
        alt={esSticker ? t('detail.sticker') : t('detail.image')}
        onError={() => media.alFallar(msg.id)}
        onLoad={() => media.alCargar(msg.id)}
        style={{
          ...dims,
          objectFit: esSticker ? 'contain' : 'cover',
          borderRadius: 8, display: 'block',
          background: esSticker ? 'transparent' : '#f1f3f5',
        }}
      />
    );
  }

  // Los tres estados sin imagen comparten caja para que el hueco no cambie de tamaño al
  // pasar de uno a otro. `sin-media` NO es `error`: el primero es permanente —expiró a
  // los 30 días, o es media anterior al paso 3— y el segundo se reintenta.
  const porEstado = {
    pendiente: { fondo: '#f1f3f5', color: '#adb5bd', texto: '' },
    'sin-media': { fondo: '#f8f9fa', color: '#868e96', texto: t('detail.mediaUnavailable') },
    error: { fondo: '#fff5f5', color: '#c92a2a', texto: t('detail.mediaError') },
  }[estado];

  return (
    <div
      ref={ref}
      data-estado={estado}
      data-testid="caja-media"
      style={{ ...cajaBase, ...dims, background: porEstado.fondo, color: porEstado.color }}
    >
      {porEstado.texto || (esSticker ? '🏷️' : '🖼️')}
    </div>
  );
}
